import { useEffect, useRef, useState } from 'react'
import { Banner, Button, Input, SectionLabel } from '../../design/components'
import { useCharacterList } from '../characters/queries'
import { useGallery } from '../images/queries'
import { useSnapshot } from './queries'
import {
  blockId,
  draftUrl,
  readImageFile,
  type ComposerBlock,
  type ComposerDraft,
  type ComposerOwner,
} from './types'
import styles from './composer.module.css'

/** Every way to put something in a prompt. Which of them a screen offers depends on the owner. */
export type ToolName =
  'text' | 'upload' | 'result' | 'character' | 'profile' | 'description' | 'place'

const LABELS: Record<ToolName, string> = {
  text: 'Add text',
  upload: 'Add image',
  result: 'Use a result',
  character: 'Character art',
  profile: 'Add profile',
  description: 'Description',
  place: "This place's images",
}

/**
 * The tools this composer offers, in the order they are useful.
 *
 * A character composer and a location composer share three tools and differ in two, and the
 * difference is a property of the subject rather than a setting: a place has no profile and a
 * character has no battle map. Both keep **Character art**, because a location prompt referencing
 * a character's art is how two pictures end up looking like the same world — even though the
 * depiction hint says not to put people *in* the picture.
 */
export function toolsFor(owner: ComposerOwner): ToolName[] {
  return owner.kind === 'character'
    ? ['text', 'upload', 'result', 'character', 'profile']
    : ['text', 'upload', 'result', 'place', 'description', 'character']
}

export interface ToolBarProps {
  owner: ComposerOwner
  open: ToolName | null
  onOpen: (tool: ToolName | null) => void
  /** True when there is a result to reuse. The tool is offered but inert without one. */
  hasDrafts: boolean
}

/**
 * The tool bar: one tap per way of adding to the prompt.
 *
 * The Blazor composers put these in a left-hand column beside the working area. That column does
 * not exist at 390px, so they became a wrapping row above the prompt — but the property that
 * mattered survives: all of them are visible at once, and none is behind a menu. A composer whose
 * tools are hidden is a text box with extra steps.
 */
export function ToolBar({ owner, open, onOpen, hasDrafts }: ToolBarProps) {
  return (
    <div className={styles.tools} role="group" aria-label="Add to the prompt">
      {toolsFor(owner).map((tool) => (
        <button
          key={tool}
          type="button"
          className={`${styles.tool} ${open === tool ? styles.toolOpen : ''}`}
          aria-pressed={open === tool}
          disabled={tool === 'result' && !hasDrafts}
          onClick={() => onOpen(open === tool ? null : tool)}
        >
          {LABELS[tool]}
        </button>
      ))}
    </div>
  )
}

export interface ToolPanelProps {
  tool: ToolName
  owner: ComposerOwner
  /** The character's or the location's name, for labelling the blocks a tool produces. */
  subjectName: string
  drafts: readonly ComposerDraft[]
  /** Some tools contribute more than one block, so this always takes a list. */
  onAdd: (blocks: ComposerBlock[]) => void
  onClose: () => void
}

/** The open tool's own form. Inline, not a modal — the app has no modals and a phone has no room. */
export function ToolPanel(props: ToolPanelProps) {
  switch (props.tool) {
    case 'text':
      return <TextTool {...props} />
    case 'upload':
      return <UploadTool {...props} />
    case 'result':
      return <ResultTool {...props} />
    case 'character':
      return <CharacterArtTool {...props} />
    case 'place':
      return <PlaceArtTool {...props} />
    case 'profile':
    case 'description':
      return <SnapshotTool {...props} />
  }
}

function TextTool({ owner, onAdd, onClose }: ToolPanelProps) {
  const [text, setText] = useState('')

  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault()
        // No title: a block someone typed is named by its own first line, and stays named
        // by it after an edit.
        onAdd([{ id: blockId(), kind: 'Text', text: text.trim() }])
      }}
    >
      <SectionLabel tone="ember" micro>
        Add text
      </SectionLabel>

      <label className={styles.field}>
        <SectionLabel tone="muted" micro>
          What to draw
        </SectionLabel>
        <textarea
          className={styles.textarea}
          value={text}
          autoFocus
          maxLength={20000}
          placeholder={
            owner.kind === 'character'
              ? 'Rain-soaked, neon behind him. Three-quarter view, waist up.'
              : 'Ground floor. The bar along the north wall, two exits, a stairwell at the back.'
          }
          onChange={(event) => setText(event.target.value)}
        />
      </label>

      <p className={styles.note}>
        Art direction, a description, a note about what to change. It reaches the model as written.
      </p>

      <Actions onClose={onClose} disabled={text.trim() === ''} action="Add to prompt" />
    </form>
  )
}

function UploadTool({ onAdd, onClose }: ToolPanelProps) {
  const [preview, setPreview] = useState<{
    dataUrl: string
    base64: string
    type: string
    name: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault()
        if (preview === null) return

        onAdd([
          {
            id: blockId(),
            kind: 'Image',
            // The file's own name: it is what the person who chose it will recognise, and
            // "Uploaded" three times over says nothing about which is which.
            title: preview.name,
            preview: preview.dataUrl,
            source: { from: 'upload', upload: { contentType: preview.type, data: preview.base64 } },
          },
        ])
      }}
    >
      <SectionLabel tone="ember" micro>
        Add an image
      </SectionLabel>

      {error !== null && <Banner tone="error">{error}</Banner>}

      <label className={styles.fileField}>
        <SectionLabel tone="muted" micro>
          File
        </SectionLabel>
        <input
          className={styles.file}
          type="file"
          accept="image/jpeg,image/png"
          onChange={async (event) => {
            setError(null)
            setPreview(null)

            const file = event.target.files?.[0]
            if (file === undefined) return

            // The same two rules the gallery enforces, checked here so a refusal costs no
            // round trip and no wait.
            if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
              setError('Reference images must be PNG or JPEG.')
              return
            }

            if (file.size > 5 * 1024 * 1024) {
              setError('Reference images must be under 5 MB.')
              return
            }

            try {
              const read = await readImageFile(file)
              setPreview({ ...read, type: file.type, name: file.name })
            } catch {
              setError('That file could not be read.')
            }
          }}
        />
      </label>

      {preview !== null && <img className={styles.preview} src={preview.dataUrl} alt="" />}

      <p className={styles.note}>
        A photograph, a floor plan, a piece of art to draw from. It travels with the prompt and is
        not saved anywhere.
      </p>

      <Actions onClose={onClose} disabled={preview === null} action="Add to prompt" />
    </form>
  )
}

/**
 * Reuse an earlier result as a reference.
 *
 * It adds **two** blocks — the picture, then a line telling the model what the picture is. That
 * pairing is the Blazor composer's, and it is the difference between "here is another
 * reference" and "here is your own last attempt; keep it and change this". Without the sentence
 * the model has no way to tell those apart.
 */
function ResultTool({ drafts, onAdd, onClose }: ToolPanelProps) {
  const [selected, setSelected] = useState<string | null>(null)

  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault()
        const draft = drafts.find((entry) => entry.draftId === selected)
        if (draft === undefined) return

        onAdd(reuseBlocks(draft))
      }}
    >
      <SectionLabel tone="ember" micro>
        Use a result
      </SectionLabel>

      <div className={styles.pickGrid}>
        {drafts.map((draft) => (
          <button
            key={draft.draftId}
            type="button"
            className={styles.pick}
            aria-pressed={selected === draft.draftId}
            aria-label={`Result from ${new Date(draft.createdAt).toLocaleTimeString()}`}
            onClick={() => setSelected(draft.draftId)}
          >
            <img className={styles.pickThumb} src={draftUrl(draft)} alt="" />
          </button>
        ))}
      </div>

      <p className={styles.note}>
        Adds the picture and a line saying it is your own last attempt — which is what turns a
        second generation into a revision rather than another roll.
      </p>

      <Actions onClose={onClose} disabled={selected === null} action="Add to prompt" />
    </form>
  )
}

/**
 * The two blocks a "refine" adds. Shared with the results card, which offers the same move.
 */
export function reuseBlocks(draft: ComposerDraft): ComposerBlock[] {
  return [
    {
      id: blockId(),
      kind: 'Image',
      title: `Earlier result · ${new Date(draft.createdAt).toLocaleTimeString()}`,
      preview: draftUrl(draft),
      source: { from: 'draft', draftId: draft.draftId },
    },
    {
      id: blockId(),
      kind: 'Text',
      text: 'The previous image is a generated result using the rest of the prompt.',
    },
  ]
}

/**
 * A picture from a character's gallery, named by id.
 *
 * By id and not by bytes: nothing is uploaded, the request stays small, and the reference is
 * subject to exactly the access check reading that gallery would be. The search is over the
 * caller's own roster, which is what the character list endpoint returns.
 */
function CharacterArtTool({ onAdd, onClose }: ToolPanelProps) {
  const [term, setTerm] = useState('')
  const [pickedCharacter, setPickedCharacter] = useState<{ id: number; name: string } | null>(null)
  const [pickedImage, setPickedImage] = useState<number | null>(null)

  // Two characters' names rarely share a prefix shorter than this, and searching on one
  // letter is a round trip per keystroke for a list nobody can read.
  const searching = term.trim().length >= 2
  const list = useCharacterList(1, 20, term, searching)
  const gallery = useGallery(
    { kind: 'character', characterId: pickedCharacter?.id ?? 0 },
    pickedCharacter !== null,
  )

  const images = gallery.data?.images ?? []
  const chosen = images.find((image) => image.id === pickedImage)

  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault()
        if (chosen?.id === undefined || pickedCharacter === null) return

        onAdd([
          {
            id: blockId(),
            kind: 'Image',
            title: `${pickedCharacter.name}: ${chosen.title ?? 'gallery image'}`,
            preview: chosen.urlThumb ?? chosen.urlSmall ?? chosen.url ?? '',
            source: { from: 'characterGallery', characterImageId: chosen.id },
          },
        ])
      }}
    >
      <SectionLabel tone="ember" micro>
        Character art
      </SectionLabel>

      <Input
        label="Search"
        value={term}
        autoFocus
        placeholder="Harry"
        hint="At least two letters. Your own characters."
        onChange={(event) => {
          setTerm(event.target.value)
          setPickedCharacter(null)
          setPickedImage(null)
        }}
      />

      {searching && list.isSuccess && list.data.items.length === 0 && (
        <p className={styles.empty}>No character matches that.</p>
      )}

      {searching && list.data !== undefined && list.data.items.length > 0 && (
        <div className={styles.hits} role="group" aria-label="Characters">
          {list.data.items.map((character) => (
            <button
              key={character.id}
              type="button"
              className={styles.hit}
              aria-pressed={pickedCharacter?.id === character.id}
              onClick={() => {
                setPickedCharacter({ id: character.id ?? 0, name: character.name ?? 'Unnamed' })
                setPickedImage(null)
              }}
            >
              <span className={styles.rowName}>{character.name}</span>
              <span className={styles.rowMeta}>{character.type}</span>
            </button>
          ))}
        </div>
      )}

      {pickedCharacter !== null && (
        <div className={styles.field}>
          <SectionLabel tone="muted" micro>
            {pickedCharacter.name}&apos;s gallery
          </SectionLabel>

          {gallery.isPending && <p className={styles.note}>Loading…</p>}
          {gallery.isSuccess && images.length === 0 && (
            <p className={styles.empty}>That character has no images yet.</p>
          )}

          <div className={styles.pickGrid}>
            {images.map((image) => (
              <button
                key={image.id}
                type="button"
                className={styles.pick}
                aria-pressed={pickedImage === image.id}
                aria-label={image.title ?? 'Gallery image'}
                onClick={() => setPickedImage(image.id ?? null)}
              >
                <img
                  className={styles.pickThumb}
                  src={image.urlThumb ?? image.urlSmall ?? image.url ?? ''}
                  alt=""
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      <Actions onClose={onClose} disabled={chosen === undefined} action="Add to prompt" />
    </form>
  )
}

/**
 * This location's own gallery — its depiction, and its battle map.
 *
 * The single most useful tool on a location composer, and the reason the battle-map hint exists:
 * adding the depiction before drawing a map is what makes the map read as *the same place*.
 * Each image says which kind it is, because "use the depiction" is the advice and a grid of
 * unlabelled thumbnails is not enough to follow it.
 */
function PlaceArtTool({ owner, subjectName, onAdd, onClose }: ToolPanelProps) {
  const [picked, setPicked] = useState<number | null>(null)

  const gallery = useGallery(
    owner.kind === 'location'
      ? { kind: 'location', campaignId: owner.campaignId, locationId: owner.locationId }
      : { kind: 'character', characterId: 0 },
    owner.kind === 'location',
  )

  const images = gallery.data?.images ?? []
  const chosen = images.find((image) => image.id === picked)

  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault()
        if (chosen?.id === undefined) return

        onAdd([
          {
            id: blockId(),
            kind: 'Image',
            title: `${subjectName}: ${chosen.title ?? kindLabel(chosen.type)}`,
            preview: chosen.urlThumb ?? chosen.urlSmall ?? chosen.url ?? '',
            source: { from: 'locationGallery', locationImageId: chosen.id },
          },
        ])
      }}
    >
      <SectionLabel tone="ember" micro>
        {subjectName}&apos;s images
      </SectionLabel>

      {gallery.isPending && <p className={styles.note}>Loading…</p>}
      {gallery.isSuccess && images.length === 0 && (
        <p className={styles.empty}>
          This place has no images yet. Draw a depiction first — a battle map made from one reads as
          the same building.
        </p>
      )}

      <div className={styles.pickGrid}>
        {images.map((image) => (
          <button
            key={image.id}
            type="button"
            className={styles.pick}
            aria-pressed={picked === image.id}
            aria-label={`${kindLabel(image.type)}: ${image.title ?? 'untitled'}`}
            onClick={() => setPicked(image.id ?? null)}
          >
            <img
              className={styles.pickThumb}
              src={image.urlThumb ?? image.urlSmall ?? image.url ?? ''}
              alt=""
              loading="lazy"
            />
            <span className={styles.pickCaption}>{kindLabel(image.type)}</span>
          </button>
        ))}
      </div>

      <Actions onClose={onClose} disabled={chosen === undefined} action="Add to prompt" />
    </form>
  )
}

function kindLabel(type: string | null | undefined): string {
  return type === 'BattleMap' ? 'Battle map' : 'Depiction'
}

/**
 * The subject's own words, pasted in and editable first.
 *
 * Editable is the point rather than a nicety. For a character the snapshot is the whole sheet in
 * prose, and most of a prompt's value is in cutting it down to what is visible in a portrait. For
 * a location it is the name and description the one-button generator would have sent on your
 * behalf — and being able to *see* that, and change it, is the entire difference between the two
 * screens.
 */
function SnapshotTool({ owner, subjectName, onAdd, onClose }: ToolPanelProps) {
  const snapshot = useSnapshot(owner, true)
  const [text, setText] = useState('')

  const isCharacter = owner.kind === 'character'
  const heading = isCharacter ? `${subjectName}'s profile` : `${subjectName}'s description`

  // Seeded once the fetch lands, and not on every render: the point is that it can be edited,
  // and re-seeding would throw away the edit on any refetch.
  const seeded = useRef(false)
  useEffect(() => {
    if (!seeded.current && snapshot.data !== undefined) {
      seeded.current = true
      setText(snapshot.data.text)
    }
  }, [snapshot.data])

  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault()
        onAdd([
          // Titled, so trimming the text down does not rename the block to whatever line
          // happens to survive at the top.
          { id: blockId(), kind: 'Text', title: heading, text: text.trim() },
        ])
      }}
    >
      <SectionLabel tone="ember" micro>
        {heading}
      </SectionLabel>

      {snapshot.isPending && (
        <p className={styles.note}>{isCharacter ? 'Reading the sheet…' : 'Reading the place…'}</p>
      )}
      {snapshot.isError && <Banner tone="error">That could not be read.</Banner>}

      {snapshot.isSuccess && (
        <label className={styles.field}>
          <SectionLabel tone="muted" micro>
            {isCharacter ? 'Profile' : 'Description'}
          </SectionLabel>
          <textarea
            className={styles.textarea}
            value={text}
            rows={12}
            maxLength={20000}
            onChange={(event) => setText(event.target.value)}
          />
        </label>
      )}

      <p className={styles.note}>
        {isCharacter
          ? 'Trim it to what a portrait can show — a face, a build, what they wear, what they carry.'
          : 'Trim it to what a picture can show — the shape of the place, what is in it, how it is lit.'}
      </p>

      <Actions onClose={onClose} disabled={text.trim() === ''} action="Add to prompt" />
    </form>
  )
}

/**
 * Cancel and confirm, for every tool.
 *
 * The confirm says "Add to prompt" rather than repeating the tool's own name: the tool bar
 * button above already says "Add text", and two controls with the same accessible name on one
 * screen is a screen reader announcing the same thing for two different jobs.
 */
function Actions({
  onClose,
  disabled,
  action,
}: {
  onClose: () => void
  disabled: boolean
  action: string
}) {
  return (
    <div className={styles.actions}>
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button type="submit" disabled={disabled}>
        {action}
      </Button>
    </div>
  )
}
